import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import SubPage, { PageAction, PageActions } from '#/common/components/SubPage';
import TMMButton from '#/common/components/TMMButton';
import { NuvoLabel } from '@nuvolo/nuux-core/components/NuvoLabel';
import WorkingOnItThrobber from '#/app/components/Throbbers/WorkingOnItThrobber';

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-bottom: 24px;
`;

const InsertThrobber = () => (
  <div
    style={{
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
    <WorkingOnItThrobber secondaryDescription="Please don't refresh or close this page" />
  </div>
);

export interface WizardStep {
  /** Content to render for this step. */
  component: React.ReactNode;
  /** Whether the user may proceed to the next step. Defaults to true. */
  isValid?: boolean;
}

export interface ImportWizardBaseProps {
  title: string;
  steps: WizardStep[];
  /** Called when the user confirms submission on the last step. */
  onSubmit: () => void;
  /** Label for the final action button. Defaults to "Submit". */
  submitLabel?: string;
  /** When true, replaces the wizard UI with a full-page throbber. */
  insertingRecords?: boolean;
  /** Modals and overlays rendered outside the wizard layout. */
  modals?: React.ReactNode;
  /** When true, shows the navigation buttons at the bottom of the wizard. */
  showButtonsOnBottom?: boolean;
  /** When provided, the back button will go back by this many page steps instead of just one. */
  goBackByHowManyPages?: number;
}

const ImportWizardBase: React.FC<ImportWizardBaseProps> = ({
  title,
  steps,
  onSubmit,
  submitLabel = 'Submit',
  insertingRecords = false,
  modals,
  showButtonsOnBottom = true,
  goBackByHowManyPages = 1,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const totalSteps = steps.length;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const currentStep = steps[currentStepIndex];

  const isNextDisabled = currentStep.isValid === false;

  const handleNext = useCallback(() => {
    if (isLastStep) {
      onSubmit();
    } else {
      setCurrentStepIndex((i) => i + 1);
    }
  }, [isLastStep, onSubmit]);

  const handleBack = useCallback(() => {
    setCurrentStepIndex((i) => Math.max(0, i - 1));
  }, []);

  useEffect(() => {
    (document.activeElement as HTMLElement)?.blur();
  }, [currentStepIndex]);

  const stepLabel = `Step ${currentStepIndex + 1}/${totalSteps}`;
  const nextButtonLabel = isLastStep ? submitLabel : 'Next Step';

  const actionsOnTop = useMemo<PageAction[]>(() => {
    const backAction: PageAction = {
      id: 'step-back-on-top',
      type: 'button',
      alwaysShow: true,
      props: {
        type: 'normal',
        label: 'Back',
        onClick: handleBack,
      },
    };

    const nextAction: PageAction = {
      id: 'step-next-on-top',
      type: 'button',
      alwaysShow: true,
      props: {
        type: 'submit',
        label: nextButtonLabel,
        onClick: handleNext,
        disabled: isNextDisabled,
      },
    };

    return currentStepIndex > 0 ? [backAction, nextAction] : [nextAction];
  }, [handleBack, handleNext, nextButtonLabel, isNextDisabled, currentStepIndex]);

  return (
    <>
      {insertingRecords ? (
        <InsertThrobber />
      ) : (
        <SubPage title={title} showGoBackButton goBackByHowManyPages={goBackByHowManyPages}>
          <PageActions actions={actionsOnTop} />
          <NuvoLabel>{stepLabel}</NuvoLabel>
          {currentStep.component}
          {showButtonsOnBottom && (
            <ButtonRow>
              <TMMButton
                type="normal"
                label="Back"
                onClick={handleBack}
                visible={currentStepIndex > 0}
              />
              <TMMButton
                type="submit"
                label={nextButtonLabel}
                onClick={handleNext}
                visible={currentStepIndex > 0}
                disabled={isNextDisabled}
              />
            </ButtonRow>
          )}
        </SubPage>
      )}
      {modals}
    </>
  );
};

export default React.memo(ImportWizardBase);
